<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue'
import DynamicForm from '@/views/DynamicForm.vue';
import clientService from '@/services/ClientService';
import {useRoute} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {setButtonContentDefault, setButtonContentLoading, showSuccessMessage} from '@/helper';
import {authData} from '@/services/AuthService';
import AmlQuestionnaireService, {visibleAMLQuestionnaireResultKeys} from '@/services/AmlQuestionnaireService';
import DocumentService from "@/services/DocumentService";

const props = defineProps({
  amlQuestionnaire: {type: Object, required: false},
  disabled: {type: Boolean, required: false, default: false},
  prefillObject: {type: Object, required: false, default: null},
  clientId: {type: Number, required: true, default: null}
});

const {t} = useI18n()
const params = useRoute().params;
const selectedForm = ref(null);
const dynamicFormRef = ref(null);
const form = ref({...props.amlQuestionnaire});
const isOpen = ref(true)
const result = ref(null);
const emit = defineEmits(['confirm', 'cancel']);
const availableQuestionnaires = ref(null);
const availableContracts = ref([]);
const selectedContractId = ref(null);
const documentIsGenerating = ref(false);
const formKey = ref(0);
const isInitialized = ref(false);

// Pro nové záznamy: zobrazit select jen když šablona obsahuje ${CLIENT_CONTRACT_NUMBER}.
// Pro editaci existujících: zobrazit vždy, pokud má klient nějaké smlouvy — uživatel
// může změnit přiřazenou smlouvu, která se použije při generování dokumentu.
const showContractSelect = computed(() =>
  !props.amlQuestionnaire?.id && (
    selectedForm.value?.has_contract_number_keyword === true ||
    availableContracts.value.length > 0
  )
);

const closeModalWith = (value, state) => {
  form.value.data = dynamicFormRef.value?.config;
  // IMA-487: propsat "Nezobrazovat dokument klientovi" ze šablony i pro samostatné
  // (mimo ASO) generování AML - DocumentService čte tento příznak z AmlQuestionnaire.data.
  if (form.value.data) {
    form.value.data.hide_document_from_client = selectedForm.value?.hide_document_from_client ?? false;
  }
  if (selectedContractId.value) {
    form.value.data = { ...(form.value.data ?? {}), _meta: { contract_id: selectedContractId.value } };
  }
  form.value.state = state;
  form.value.method = 'FORM';
  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      if (!state) {
        setButtonContentLoading('save-button');
        if (selectedForm.value.confirmation_required) {
          form.value.state = 'WAITING';
        } else {
          form.value.state = 'TO_REVIEW';
        }
      } else {
        setButtonContentLoading('concept-button');
      }
      emit('confirm', form.value);
    } else {
      emit('cancel');
    }
  }, 300);
}

function openModal() {
  isOpen.value = true
}

onMounted(() => {
  if (props.amlQuestionnaire?.result) {
    result.value = props.amlQuestionnaire.result;
  }

  clientService.getAllActiveTemplates(params.id, 'AML_QUESTIONNAIRE')
      .then(({ templates, contracts }) => {
        availableQuestionnaires.value = templates?.map(form => ({
          ...form.template,
          id: form.id,
          has_contract_number_keyword: form.has_contract_number_keyword ?? false,
        }));
        availableContracts.value = contracts ?? [];
        selectedContractId.value = contracts?.[0]?.id ?? null;

        if (props.amlQuestionnaire?.data) {
          selectedForm.value = props.amlQuestionnaire.data;
          selectedContractId.value = props.amlQuestionnaire.data?._meta?.contract_id ?? selectedContractId.value;
        } else {
          selectedForm.value = null;
        }
        nextTick(() => { isInitialized.value = true; });
      })
})

// Při změně smlouvy znovu načte všechny šablony s novým contract_id, aby se
function mergeAnswersIntoTemplate(freshTemplate, currentForm) {
  if (!currentForm?.content || !freshTemplate?.content) return freshTemplate;
  const currentValues = {};
  for (const page of currentForm.content) {
    for (const question of page.questions ?? []) {
      if (question.id != null) currentValues[question.id] = question.value;
    }
  }
  return {
    ...freshTemplate,
    content: freshTemplate.content.map(page => ({
      ...page,
      questions: (page.questions ?? []).map(q => ({
        ...q,
        value: q.id in currentValues ? currentValues[q.id] : q.value,
      })),
    })),
  };
}

watch(selectedContractId, (newContractId) => {
  if (!selectedForm.value || !isInitialized.value) return;
  const currentId = selectedForm.value.id;
  const currentForm = selectedForm.value;
  clientService.getAllActiveTemplates(params.id, 'AML_QUESTIONNAIRE', newContractId)
    .then(({ templates }) => {
      availableQuestionnaires.value = templates?.map(t => ({
        ...t.template,
        id: t.id,
        has_contract_number_keyword: t.has_contract_number_keyword ?? false,
      }));
      const fresh = availableQuestionnaires.value?.find(t => t.id === currentId) ?? null;
      selectedForm.value = fresh ? mergeAnswersIntoTemplate(fresh, currentForm) : null;
      formKey.value++;
    });
});

function countResult() {
  setButtonContentLoading('count-button');
  const formValue = {
    ...form.value,
    data: dynamicFormRef.value?.config,
    client_id: props.clientId,
    creator_id: authData.loggedUser.id,
    method: 'FORM'
  };
  if(!formValue.state) {
    formValue.state = 'CONCEPT';
  }

  AmlQuestionnaireService.countResult(props.clientId, formValue.id, formValue).then(response => {
    result.value = response.questionnaire.result;
    form.value = {...response.questionnaire};
    setButtonContentDefault('count-button');
  });
}

function generateDocument() {
  documentIsGenerating.value = true;
  const body = {
    model_id: form.value.id,
    model_type: '\\App\\Models\\AmlQuestionnaire',
  }
  DocumentService.generateDocument(body).then(response => {
    documentIsGenerating.value = false;
    showSuccessMessage(t('general.document_generated_successfully'));
    emitter.emit('documentGenerated');
  })
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ t('AmlQuestionnaireModal.aml_questionnaire') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')" id="amlQuestionnaireForm">
              <div class="modal-body">
                <div v-if="!form.id" class="row mb-3">
                  <div class="form-group">
                    <label for="formTypeSelect" class="form-label">{{ t('AmlQuestionnaireModal.select_form') }}</label>
                    <select id="formTypeSelect" v-model="selectedForm" class="form-select">
                      <option v-for="form of availableQuestionnaires" :value="form">{{form.name}}</option>
                    </select>
                  </div>
                </div>
                <div v-if="showContractSelect" class="row mb-3">
                  <div class="form-group">
                    <label for="contractSelect" class="form-label">{{ t('general.contract') }}</label>
                    <select id="contractSelect" v-model="selectedContractId" class="form-select">
                      <option v-for="contract of availableContracts" :key="contract.id" :value="contract.id">
                        {{ contract.contract_number }}
                      </option>
                    </select>
                  </div>
                </div>
                <div v-if="availableQuestionnaires?.length === 0 && !props.disabled" class="alert alert-danger"> {{ t('AmlQuestionnaireModal.no_forms_available') }} </div>
                <DynamicForm :key="(selectedForm?.id ?? 0) + '-' + formKey" v-if="selectedForm" :disabled="props.disabled" :config="selectedForm" ref="dynamicFormRef" :prefill-object="props.prefillObject"></DynamicForm>
              </div>

              <div v-if="selectedForm" class="row mb-3">
                <div class="col-12">
                  <hr/>
                  <fieldset>
                    <legend>{{ t('AmlQuestionnaireModal.result') }}</legend>
                    <table class="table">
                      <thead>
                        <tr>
                          <th scope="col">{{ t('AmlQuestionnaireModal.metric') }}</th>
                          <th scope="col">{{ t('AmlQuestionnaireModal.review') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="key in visibleAMLQuestionnaireResultKeys">
                          <td scope="row" :class="{ 'fw-bold': key === 'CLIENT_CHECK_INTENSITY' }">{{ t('AmlQuestionnaireModal.' + key) }}</td>
                          <td>{{ result?.[key] || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </fieldset>
                </div>
              </div>

              <div class="modal-footer">
                <button v-if="!amlQuestionnaire?.id && !props.disabled" class="btn btn-link text-decoration-underline text-dark" type="button" @click="closeModalWith('cancel')">{{ t('AmlQuestionnaireModal.cancel') }}</button>
                <button v-if="!props.disabled && availableQuestionnaires?.length > 0" :disabled="!selectedForm" class="btn btn-primary btn-md" type="button" @click="countResult()" id="count-button"><span>{{ t('AmlQuestionnaireModal.evaluate') }}</span></button>
                <button v-if="!props.disabled && availableQuestionnaires?.length > 0" :disabled="!selectedForm" class="btn btn-primary btn-md" type="button" @click="closeModalWith('confirm', 'CONCEPT')">{{ t('AmlQuestionnaireModal.save_as_concept') }}</button>
                <button v-if="!props.disabled && availableQuestionnaires?.length > 0" :disabled="!selectedForm" class="btn btn-primary btn-md" type="submit">{{ t('AmlQuestionnaireModal.save_and_send_to_client') }}</button>
                <button v-if="amlQuestionnaire?.id" @click="generateDocument" class="btn btn-primary btn-md" type="button" id="generate-document-button"><span v-if="!documentIsGenerating">{{ t('general.generate_document') }}</span><span v-else>{{ t('general.generating') }}</span></button>

              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

